import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionApi } from '../services/api';

interface Tier {
  id: number;
  name: string;
  description: string | null;
  text_limit: number;
  monthly_limit: number;
  weekly_cost_limit: string | null;
  monthly_price: string | null;
  yearly_price: string | null;
  features: Record<string, any> | null;
  is_default: boolean;
  priority: number;
}

interface CurrentSubscription {
  has_subscription: boolean;
  subscription: {
    id: number;
    status: string;
    tier_id: number;
    tier_name: string;
    amount: string;
    currency: string;
    interval: string;
    next_payment_date: string | null;
    ends_at: string | null;
    cancelled_at: string | null;
  } | null;
  tier: {
    id: number;
    name: string;
    description: string;
    text_limit: number;
    monthly_limit: number;
    weekly_cost_limit: string | null;
    monthly_price: string | null;
    yearly_price: string | null;
    priority: number;
  } | null;
  subscription_status: string | null;
  subscription_ends_at: string | null;
}

const SubscriptionPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedInterval, setSelectedInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [processingTierId, setProcessingTierId] = useState<number | null>(null);

  // Fetch current subscription
  const { data: currentSubscription, isLoading: loadingCurrent } = useQuery({
    queryKey: ['currentSubscription'],
    queryFn: () => subscriptionApi.getCurrent().then((r) => r.data as CurrentSubscription),
  });

  // Fetch available tiers
  const { data: tiersData, isLoading: loadingTiers } = useQuery({
    queryKey: ['availableTiers'],
    queryFn: () => subscriptionApi.getTiers().then((r) => r.data),
  });

  // Subscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: ({ tierId, interval }: { tierId: number; interval: string }) =>
      subscriptionApi.subscribe(tierId, interval),
    onSuccess: (response) => {
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
      queryClient.invalidateQueries({ queryKey: ['currentSubscription'] });
    },
    onSettled: () => setProcessingTierId(null),
  });

  // Upgrade mutation
  const upgradeMutation = useMutation({
    mutationFn: ({ tierId, interval }: { tierId: number; interval: string }) =>
      subscriptionApi.upgrade(tierId, interval),
    onSuccess: (response) => {
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
      queryClient.invalidateQueries({ queryKey: ['currentSubscription'] });
    },
    onSettled: () => setProcessingTierId(null),
  });

  // Downgrade mutation
  const downgradeMutation = useMutation({
    mutationFn: ({ tierId, interval }: { tierId: number; interval: string }) =>
      subscriptionApi.downgrade(tierId, interval),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentSubscription'] });
    },
    onSettled: () => setProcessingTierId(null),
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => subscriptionApi.cancel(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentSubscription'] });
    },
  });

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: () => subscriptionApi.reactivate(),
    onSuccess: (response) => {
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
      queryClient.invalidateQueries({ queryKey: ['currentSubscription'] });
    },
  });

  const handleTierAction = (tier: Tier) => {
    setProcessingTierId(tier.id);

    const currentTierPriority = currentSubscription?.tier?.priority ?? -1;
    const hasActiveSubscription = currentSubscription?.subscription_status === 'active';

    if (!hasActiveSubscription) {
      // Start new subscription
      subscribeMutation.mutate({ tierId: tier.id, interval: selectedInterval });
    } else if (tier.priority > currentTierPriority) {
      // Upgrade
      upgradeMutation.mutate({ tierId: tier.id, interval: selectedInterval });
    } else if (tier.priority < currentTierPriority) {
      // Downgrade
      if (window.confirm(`Downgrade to ${tier.name}? This will take effect at the end of your current billing period.`)) {
        downgradeMutation.mutate({ tierId: tier.id, interval: selectedInterval });
      } else {
        setProcessingTierId(null);
      }
    }
  };

  const getButtonText = (tier: Tier) => {
    const currentTierPriority = currentSubscription?.tier?.priority ?? -1;
    const currentTierId = currentSubscription?.tier?.id;
    const hasActiveSubscription = currentSubscription?.subscription_status === 'active';

    if (processingTierId === tier.id) return 'Processing...';
    if (currentTierId === tier.id && hasActiveSubscription) return 'Current Plan';
    if (!hasActiveSubscription) return 'Subscribe';
    if (tier.priority > currentTierPriority) return 'Upgrade';
    if (tier.priority < currentTierPriority) return 'Downgrade';
    return 'Select';
  };

  const isCurrentTier = (tier: Tier) => {
    return currentSubscription?.tier?.id === tier.id && currentSubscription?.subscription_status === 'active';
  };

  if (loadingCurrent || loadingTiers) {
    return <div className="animate-pulse">Loading...</div>;
  }

  const tiers: Tier[] = tiersData?.tiers || [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Subscription</h1>
        <p className="text-gray-500 dark:text-gray-400">Manage your subscription and billing</p>
      </div>

      {/* Current Subscription Status */}
      {currentSubscription?.subscription && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Current Subscription</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Plan</span>
              <p className="font-medium text-gray-900 dark:text-white">
                {currentSubscription.subscription.tier_name || currentSubscription.tier?.name}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
              <p className="font-medium">
                <span className={`px-2 py-0.5 text-xs rounded ${
                  currentSubscription.subscription.status === 'active'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : currentSubscription.subscription.status === 'cancelled'
                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {currentSubscription.subscription.status}
                </span>
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {currentSubscription.subscription.cancelled_at ? 'Access Until' : 'Next Payment'}
              </span>
              <p className="font-medium text-gray-900 dark:text-white">
                {currentSubscription.subscription.cancelled_at
                  ? new Date(currentSubscription.subscription.ends_at || '').toLocaleDateString()
                  : currentSubscription.subscription.next_payment_date
                  ? new Date(currentSubscription.subscription.next_payment_date).toLocaleDateString()
                  : '-'}
              </p>
            </div>
          </div>

          {/* Cancel / Reactivate buttons */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {currentSubscription.subscription.status === 'active' ? (
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel? You will retain access until the end of your billing period.')) {
                    cancelMutation.mutate();
                  }
                }}
                disabled={cancelMutation.isPending}
                className="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            ) : currentSubscription.subscription.status === 'cancelled' ? (
              <button
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate Subscription'}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* Interval Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
          <button
            onClick={() => setSelectedInterval('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedInterval === 'monthly'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setSelectedInterval('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedInterval === 'yearly'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            Yearly <span className="text-green-600 dark:text-green-400">(Save 25%)</span>
          </button>
        </div>
      </div>

      {/* Tier Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((tier) => {
          const price = selectedInterval === 'monthly' ? tier.monthly_price : tier.yearly_price;
          const isCurrent = isCurrentTier(tier);
          const priceNum = price ? parseFloat(price) : 0;

          return (
            <div
              key={tier.id}
              className={`card p-6 relative ${isCurrent ? 'ring-2 ring-primary-500' : ''}`}
            >
              {isCurrent && (
                <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full">
                  Current
                </span>
              )}

              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{tier.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{tier.description}</p>

              <div className="mt-4">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {priceNum > 0 ? `€${price}` : 'Free'}
                </span>
                {priceNum > 0 && (
                  <span className="text-gray-500 dark:text-gray-400">
                    /{selectedInterval === 'monthly' ? 'month' : 'year'}
                  </span>
                )}
              </div>

              {/* Show monthly equivalent for yearly */}
              {selectedInterval === 'yearly' && priceNum > 0 && tier.monthly_price && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  (€{(priceNum / 12).toFixed(2)}/month)
                </p>
              )}

              <ul className="mt-6 space-y-3">
                <li className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {tier.text_limit.toLocaleString()} chars/analysis
                </li>
                <li className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {tier.monthly_limit} analyses/month
                </li>
              </ul>

              <button
                onClick={() => !isCurrent && handleTierAction(tier)}
                disabled={isCurrent || processingTierId !== null}
                className={`w-full mt-6 btn ${
                  isCurrent
                    ? 'btn-secondary cursor-not-allowed'
                    : tier.priority > (currentSubscription?.tier?.priority ?? -1)
                    ? 'btn-primary'
                    : 'btn-secondary'
                }`}
              >
                {getButtonText(tier)}
              </button>
            </div>
          );
        })}
      </div>

      {/* Payment History */}
      <PaymentHistory />
    </div>
  );
};

// Payment History Component
const PaymentHistory: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['paymentHistory'],
    queryFn: () => subscriptionApi.getPaymentHistory().then((r) => r.data),
  });

  if (isLoading) return null;

  const payments = data?.items || [];

  if (payments.length === 0) return null;

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment History</h2>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Description</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {payments.map((payment: any) => (
              <tr key={payment.id}>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {new Date(payment.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {payment.description || payment.payment_type}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {payment.currency} {payment.amount}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    payment.status === 'paid'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : payment.status === 'failed'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {payment.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubscriptionPage;
